import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ObjectId } from 'bson';
import { useSystem } from '../../../hooks/useSystem';
import { SessionUserLoaderWidget } from '../../../widgets/content/SessionUserLoaderWidget';
import { ButtonType, ButtonWidget } from '../../../widgets/controls/ButtonWidget';
import { Notification, NotificationType } from '../../../core/stores/NotificationStore';
import { ImageModel } from '../../../core/models/ImageModel';
import { ProfileImageModel } from '../../../core/models/ProfileImageModel';
import { AnimatePageWidget } from '../../../widgets/layout/AnimatePageWidget';

export const EditImagePage: React.FC = observer(() => {
  return (
    <SessionUserLoaderWidget>
      {() => {
        return <EditImageContent />;
      }}
    </SessionUserLoaderWidget>
  );
});

export const EditImageContent: React.FC = observer(() => {
  const system = useSystem();
  const navigate = useNavigate();
  const params = useParams<'id'>();
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [image, setImage] = useState<ImageModel | null>(null);

  useEffect(() => {
    const id = params.id;
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    let image_id: ObjectId;
    try {
      image_id = new ObjectId(id);
    } catch {
      setImage(null);
      setLoading(false);
      return;
    }
    system.session.api_client.profile
      .getProfileGalleryImage({
        _id: image_id
      })
      .then((res) => {
        if (!cancelled) {
          setImage(new ProfileImageModel(res, system.user));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setImage(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [params.id, system]);

  return (
    <AnimatePageWidget slide={true}>
      <S.Container>
        {loading || !image ? null : <S.Image src={image.url} />}
        <S.Actions>
          <ButtonWidget
            label="Back"
            type={ButtonType.SECONDARY}
            action={() => {
              navigate(-1);
            }}
          />
          <ButtonWidget
            label="Delete"
            loading={deleting}
            disabled={!image || !image.canEdit()}
            type={ButtonType.SECONDARY}
            action={async () => {
              if (!image) {
                return;
              }
              const confirm = await system.dialog_store.showConfirmDialog({
                title: 'Delete image?',
                desc: 'This will remove the image from your gallery.'
              });
              if (!confirm) {
                return;
              }
              setDeleting(true);
              try {
                await image.delete();
                system.notification_store.showNotification(
                  new Notification({
                    type: NotificationType.SUCCESS,
                    desc: 'Image deleted'
                  })
                );
                navigate(system.user.link, { state: 'push' });
              } catch {
                system.notification_store.showNotification(
                  new Notification({
                    type: NotificationType.ERROR,
                    desc: 'Failed to delete image'
                  })
                );
              } finally {
                setDeleting(false);
              }
            }}
          />
        </S.Actions>
      </S.Container>
    </AnimatePageWidget>
  );
});

namespace S {
  export const Container = styled.div`
    display: flex;
    align-items: stretch;
    flex-direction: column;
    row-gap: 10px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: auto;
    height: 100%;
    width: 100%;
    max-width: 500px;
    padding: 10px;
    box-sizing: border-box;
  `;

  export const Image = styled.img`
    width: 100%;
    max-width: 100%;
    display: block;
    border: solid 1px ${(p) => p.theme.forms.border};
    border-radius: 5px;
    box-sizing: border-box;
    aspect-ratio: 1/1;
    object-fit: contain;
  `;

  export const Actions = styled.div`
    display: flex;
    gap: 10px;
  `;
}
